import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getProfileImageReports,
  handleProfileImageReport,
  ProfileImageReport,
} from '../../../../api/report/report-profile-image.api';
import { ImageOff } from 'lucide-react';
import { AdminButton, AdminPageHeader, AdminPillTabs } from '../../ui';
import { KvRow, ReasonPill, ReportKv } from '../report-moderation/ReportParts';


const REASON_LABEL: Record<number, string> = {
  0: 'Spam',
  1: 'Không phù hợp',
  2: 'Thù ghét',
  3: 'Bạo lực',
  4: 'Quấy rối',
  5: 'Vi phạm SHTT',
  6: 'Tự hại',
  7: 'Sai lệch thông tin',
  8: 'Nguy hại trẻ em',
  9: 'Nội dung nhạy cảm',
  10: 'Khác',
};

const STATE_TABS = [
  { label: 'Chờ duyệt', value: 0 },
  { label: 'Đã gỡ', value: 1 },
  { label: 'Đã bỏ qua', value: 2 },
];

const ManagementReportProfileImage = () => {
  const [state, setState] = useState<number>(0);
  const [items, setItems] = useState<ProfileImageReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getProfileImageReports(state, 1, 50)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const onHandle = async (id: string, accept: boolean) => {
    setBusyId(id);
    try {
      await handleProfileImageReport(id, accept);
      toast.success(accept ? 'Đã gỡ ảnh + cảnh cáo người dùng' : 'Đã bỏ qua báo cáo');
      load();
    } catch {
      toast.error('Thao tác thất bại');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Ảnh đại diện / bìa bị báo cáo"
        description="Xem ảnh bị báo cáo rồi gỡ ảnh kèm cảnh cáo, hoặc bỏ qua nếu ảnh không vi phạm."
      />

      <section className="adm-stack">
        <AdminPillTabs
          ariaLabel="Lọc trạng thái báo cáo"
          items={STATE_TABS.map((t) => ({ key: t.value, label: t.label }))}
          value={state}
          onChange={(k) => setState(k)}
        />

        {loading ? (
          <p className="adm-report-empty">Đang tải…</p>
        ) : items.length === 0 ? (
          <p className="adm-report-empty">Không có báo cáo nào.</p>
        ) : (
          <div className="adm-report-grid">
            {items.map((r) => (
              <div key={r.id} className="adm-report-card adm-report-card--media">
                <div className="adm-report-card__media">
                  <span className="adm-report-card__tag">{r.imageType === 'cover' ? 'Ảnh bìa' : 'Ảnh đại diện'}</span>
                  {r.imageUrl ? (
                    <img src={r.imageUrl} alt="" />
                  ) : (
                    <>
                      <ImageOff size={28} aria-hidden="true" />
                      (ảnh đã bị gỡ / trống)
                    </>
                  )}
                </div>
                <div className="adm-report-card__pad">
                  <ReportKv>
                    <KvRow label="Chủ ảnh">
                      <span className="adm-report-kv__strong">{r.targetUserName || r.targetUserId.slice(0, 8)}</span>
                    </KvRow>
                    <KvRow label="Người báo cáo">{r.reportByUserName || r.reportByUserId.slice(0, 8)}</KvRow>
                    <KvRow label="Lý do">
                      <ReasonPill>{REASON_LABEL[r.reason] ?? 'Khác'}</ReasonPill>
                    </KvRow>
                  </ReportKv>
                </div>
                {r.content && (
                  <div className="adm-report-card__pad">
                    <div className="adm-report-quote">“{r.content}”</div>
                  </div>
                )}
                {state === 0 && (
                  <div className="adm-report-card__pad">
                    <div className="adm-report-actions">
                      <AdminButton variant="danger" size={36} disabled={busyId === r.id} onClick={() => onHandle(r.id, true)}>
                        Gỡ ảnh + cảnh cáo
                      </AdminButton>
                      <AdminButton variant="outline" size={36} disabled={busyId === r.id} onClick={() => onHandle(r.id, false)}>
                        Bỏ qua
                      </AdminButton>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ManagementReportProfileImage;
